import { useMemo } from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Badge } from '../ui/badge';
import { ImageThumbnailGrid } from '../ui/ImageThumbnailGrid';

export interface CaseAttachmentPhoto {
  id: string;
  bookingId: string;
  r2Key: string;
  contentType: string;
  fileSize: number;
  originalFilename?: string | null;
  uploadedAt?: number | null;
}

export interface CaseAttachmentsPayload {
  case: unknown;
  bookingIds: string[];
  attachments: {
    photos: CaseAttachmentPhoto[];
    signatures: CaseAttachmentPhoto[];
    files: CaseAttachmentPhoto[];
  };
}

interface CaseAttachmentsProps {
  attachments: CaseAttachmentsPayload['attachments'];
  apiBase: string;
  loading?: boolean;
}

export function CaseAttachments({ attachments, apiBase, loading = false }: CaseAttachmentsProps) {
  const photoItems = useMemo(
    () =>
      attachments.photos.map((photo) => ({
        id: photo.id,
        url: `${apiBase}/api/bookings/${photo.bookingId}/images/${photo.id}`,
        originalFilename: photo.originalFilename ?? `Foto ${photo.id.slice(0, 8)}`,
        uploadedAt: photo.uploadedAt ?? undefined,
      })),
    [attachments.photos, apiBase]
  );

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <p className="text-sm font-semibold">Adjuntos</p>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-4 w-32 mb-3" />
          <Skeleton className="h-32 w-full rounded-lg" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold">Adjuntos</p>
          <p className="text-xs text-muted-foreground">Fotos y documentos asociados al caso</p>
        </div>
        <Badge variant="outline">{attachments.photos.length} fotos</Badge>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        <ImageThumbnailGrid
          images={photoItems}
          emptyMessage="Este caso no tiene fotos"
          columns={3}
        />
        <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-3">
          {[{
            label: 'Firmas',
            count: attachments.signatures.length,
          }, {
            label: 'Documentos',
            count: attachments.files.length,
          }, {
            label: 'Reservas relacionadas',
            count: new Set(attachments.photos.map((photo) => photo.bookingId)).size,
          }].map((stat) => (
            <div key={stat.label} className="rounded border border-border bg-muted p-2 text-center">
              <p className="text-[11px] uppercase tracking-wider">{stat.label}</p>
              <p className="text-sm font-semibold text-foreground">{stat.count}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
